import { Injectable } from '@angular/core';
import { BlobServiceClient } from '@azure/storage-blob';
import { Subject, forkJoin } from 'rxjs';
import { PatchUploadModel } from '../models/common/patchUploadModel';
import { PatchUploadErrors, PatchUploadStatus } from '../models/common/patchUploadStatus';
import { PatchManagementService } from './patch-management.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { protectedResources } from '../b2c-config';
import { AuthService } from '../utilities/auth/auth.service';
import { BroadcastMessageEvent } from '../core/constants/BroadcastMessageEvent';
import { MessageBroadcastService } from './message-broadcast.service';

@Injectable({
  providedIn: 'root'
})
export class BlobServiceClientService {
  private ControllerObjList: Array<any> = [];
  private uploadStatus$ = new Subject<any>();
  private acivePatchUploads = 0;
  constructor(private patchManagementService: PatchManagementService, public dialog: MatDialog,
     private translocoService: TranslocoService, private authService: AuthService,private messageBroadcastService:MessageBroadcastService) {
  }

  async upload(patchUploadModel: PatchUploadModel) {
    const controller = new AbortController();
    this.ControllerObjList.push({ id: patchUploadModel.id, controllerObj: controller, status: PatchUploadStatus.UploadInProgress });
    const blobServiceClient = new BlobServiceClient(`https://${patchUploadModel.account}.blob.core.windows.net?${patchUploadModel.token}`);
    const containerClient = blobServiceClient.getContainerClient(patchUploadModel.container);
    const blockBlobClient = containerClient.getBlockBlobClient(`${patchUploadModel.path}/${patchUploadModel.file.name}`);
    try {
      this.incrementActiveUploadCount();
      const response = await blockBlobClient.uploadData(patchUploadModel.file, {
        abortSignal: controller.signal,
        // onProgress: (ev) => {
        //   this.progressHandler(ev, id);
        // },
        blobHTTPHeaders: { blobContentType: patchUploadModel.file.type }
      });
      this.patchManagementService.updatePatchUpload({ eventCode: PatchUploadStatus.UploadCompleted, id: patchUploadModel.id }).subscribe(
        () => {
          this.progressHandler({ status: PatchUploadStatus.UploadCompleted, id: patchUploadModel.id, comments: "" });
          const index = this.ControllerObjList.findIndex(x => x.id == patchUploadModel.id);
          if (index > -1)
            this.ControllerObjList.splice(index, 1);
        }
      );

      this.decrementActiveUploadCount();
    }
    catch (error: any) {
      if (error.name != PatchUploadErrors.Aborted) {
        this.patchManagementService.updatePatchUpload({ eventCode: PatchUploadStatus.UploadFailed, id: patchUploadModel.id, comments: error?.code }).subscribe(() => {
          this.progressHandler({ status: PatchUploadStatus.UploadFailed, id: patchUploadModel.id, comments: error });
        });
      }
      this.decrementActiveUploadCount();
    }
  }

  private progressHandler(uploadStatus) {
    const index = this.ControllerObjList.findIndex(x => x.id == uploadStatus.id);
    if (index > -1) {
      this.ControllerObjList[index].status = uploadStatus.status;
      this.uploadStatus$.next({});
    }
  }

  public cancelPatchUpload(id): Promise<boolean> {
    return new Promise((resolve) => {
      const index = this.ControllerObjList.findIndex(x => x.id == id);
      if (index > -1) {
        this.ControllerObjList[index].controllerObj?.abort();
      }
      this.patchManagementService.updatePatchUpload({ id: id, eventCode: PatchUploadStatus.UploadCancelled }).subscribe(() => {
        this.uploadStatus$.next({});
        this.ControllerObjList.splice(index, 1);
        return resolve(true);
      }
        , () => {
          this.ControllerObjList.splice(index, 1);
          return resolve(false);
        });

    })

  }

  getUploadStatus(): Subject<any> {
    return this.uploadStatus$;
  }

  incrementActiveUploadCount() {
    this.acivePatchUploads++;
  }
  decrementActiveUploadCount() {
    this.acivePatchUploads--;
  }

  isUploadInProgress() {
    return this.acivePatchUploads > 0
  }

  cancelAllPatchUploads(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const subscription: Array<any> = [];
      this.ControllerObjList.filter(x => x.status == PatchUploadStatus.UploadInProgress)?.forEach((upload) => {
        upload.controllerObj?.abort();
        subscription.push(this.patchManagementService.updatePatchUpload({ id: upload.id, eventCode: PatchUploadStatus.UploadCancelled }));
      });
      forkJoin(subscription).subscribe({
        next: () => {
          this.ControllerObjList = [];
          resolve(true);
        }
      })
    })

  }

  handleNetworkOutage() {
    const uploads: any = [];
    this.ControllerObjList.forEach((upload) => {
      upload.controllerObj?.abort();
      upload.status = PatchUploadStatus.UploadCancelled
      uploads.push(upload.id);
    });
    localStorage.setItem("failedPatchUploads", JSON.stringify(this.ControllerObjList));
    this.uploadStatus$.next({ event: PatchUploadErrors.NetworkError, items: uploads });
    this.messageBroadcastService.broadCast({event:BroadcastMessageEvent.networkOutage,data:uploads});
  }
  handleNetworkReconnection(): Promise<boolean> {

    return new Promise((resolve) => {
      const failedItems = localStorage.getItem("failedPatchUploads");
      if (failedItems && JSON.parse(failedItems).length > 0) {
        const subscription: Array<any> = [];
        const failedPatchUploads = JSON.parse(failedItems);
        failedPatchUploads.forEach((x) => {
          subscription.push(this.patchManagementService.updatePatchUpload({ id: x.id, eventCode: PatchUploadStatus.UploadFailed, comments: PatchUploadErrors.NetworkError }))
        });
        forkJoin(subscription).subscribe({
          next: () => {
            localStorage.removeItem("failedPatchUploads");
            resolve(true);
          }
          , error: () => {
            resolve(true);
          }
        });
      }
      else {
        resolve(true);
      }
    })
  }
  abortUploads() {
    this.ControllerObjList?.forEach((x => x.controllerObj.abort()));
  }

  getAllInProgressUploadsFromClient() {
    return this.ControllerObjList.filter(x => x.status == PatchUploadStatus.UploadInProgress);
  }
  handlePatchUploadOnBrowserClose() {
    const activeUploads = this.getAllInProgressUploadsFromClient();
    if (activeUploads.length > 0) {
      const token = this.authService.getToken();
      activeUploads.forEach(upload => {
        const requestOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ id: upload.id, eventCode: PatchUploadStatus.UploadCancelled }),
          keepalive: true
        };
        fetch(`${protectedResources.realizerApi.endpoint}api/ReleaseManagement/UpdateRelease`, requestOptions);
      })
    }
  }
  getIsUploadInProgress(id): boolean {
    return this.ControllerObjList.some(x => x.id == id);
  }

  abortUploadAtClient(id){
    const index = this.ControllerObjList.findIndex(x => x.id == id);
    if(index > -1){
      this.ControllerObjList[index].controllerObj.abort();
    }
  }
}
